import React, { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import {
    Megaphone, Plus, X, ChevronRight, ChevronLeft, ChevronDown,
    Users, Ticket, Layers, CalendarClock, Loader2, CheckCircle2, AlertCircle, XCircle, Info,
} from 'lucide-react';
import emailCampaignsApi from 'api/requests/emailCampaigns';
import Button from 'components/_UI/Button';
import DashCollapse from 'components/_UI/DashCollapse';
import Loader from 'components/_UI/Loader';
import Modal from 'components/_UI/Modal';
import Text from 'components/_UI/Text';
import EmailCampaignComposer, { MAX_CAMPAIGNS_PER_EVENT } from './EmailCampaignComposer';
import EmailCampaignDetailPanel from './EmailCampaignDetailPanel';
import { Column } from 'styled/alignment/Column';
import { FullyCentered } from 'styled/alignment/Center';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import usePanelPermissions from 'hooks/usePanelPermissions';
import { PERMISSIONS } from 'helpers/panelPermissions';

const AUDIENCE_ICONS = {
    customers: Users,
    event_participants: Ticket,
    event_combination: Layers,
};

const STATUS_META = {
    scheduled: { icon: CalendarClock, label: 'Agendada', tone: 'accent' },
    queued: { icon: Loader2, label: 'Na fila', tone: 'neutral', spin: true },
    resolving: { icon: Loader2, label: 'Calculando público', tone: 'neutral', spin: true },
    sending: { icon: Loader2, label: 'Enviando', tone: 'neutral', spin: true },
    sent: { icon: CheckCircle2, label: 'Enviada', tone: 'success' },
    failed: { icon: AlertCircle, label: 'Falhou', tone: 'danger' },
    canceled: { icon: XCircle, label: 'Cancelada', tone: 'neutral' },
};

const STATUS_TABS = [
    { value: '', label: 'Todas' },
    { value: 'scheduled', label: 'Agendadas' },
    { value: 'sent', label: 'Enviadas' },
    { value: 'failed', label: 'Falharam' },
    { value: 'canceled', label: 'Canceladas' },
];

function formatDateTime(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR');
}

/* Mesma distinção usada no resto da tela de clientes — sem `response`, o
   problema é a conexão do produtor, não algo que o backend explique. */
function getErrorMessage(err, fallback) {
    if (!err?.response) {
        return 'Sem conexão com o servidor. Verifique sua internet e tente novamente.';
    }
    return err?.response?.data?.message || fallback;
}

function audienceName(campaign) {
    if (campaign.audience_type === 'event_participants') {
        return `Participantes de ${campaign.event?.name || 'evento removido'}`;
    }
    if (campaign.audience_type === 'event_combination') {
        return `Combinação de ${campaign.events?.length || 0} eventos`;
    }
    return 'Todos os clientes';
}

function audienceLabel(campaign) {
    return campaign.paid_only ? `${audienceName(campaign)} · só ingressos pagos` : audienceName(campaign);
}

export default function EmailCampaignsPanel({ events = [], toast }) {
    const { can } = usePanelPermissions();
    const [composerOpen, setComposerOpen] = useState(false);
    const [composerKey, setComposerKey] = useState(0);

    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [campaigns, setCampaigns] = useState([]);
    const [campaignsLoading, setCampaignsLoading] = useState(true);
    const [campaignsError, setCampaignsError] = useState(null);

    const [campaignToCancel, setCampaignToCancel] = useState(null);
    const [cancelingCampaign, setCancelingCampaign] = useState(false);
    const [detailCampaignId, setDetailCampaignId] = useState(null);

    useEffect(() => {
        fetchCampaigns(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    useEffect(() => {
        fetchCampaigns(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    async function fetchCampaigns(pageValue = 1) {
        setCampaignsLoading(true);
        setCampaignsError(null);
        try {
            const { data } = await emailCampaignsApi.list({
                per_page: 8,
                page: pageValue,
                status: statusFilter || undefined,
            });
            setCampaigns(data?.data || []);
            setPage(data?.meta?.current_page || pageValue);
            setLastPage(data?.meta?.last_page || 1);
            setTotal(data?.meta?.total ?? (data?.data || []).length);
        } catch (err) {
            setCampaignsError(getErrorMessage(err, 'Erro ao carregar campanhas.'));
        } finally {
            setCampaignsLoading(false);
        }
    }

    function openComposer() {
        setComposerOpen(true);
    }

    function closeComposer() {
        setComposerOpen(false);
        setComposerKey(k => k + 1);
    }

    function handleCampaignCreated({ scheduled } = {}) {
        toast.success(scheduled ? 'Campanha agendada!' : 'Campanha criada! O envio começa em instantes.');
        closeComposer();
        setStatusFilter('');
        fetchCampaigns(1);
    }

    function changeStatusFilter(value) {
        setStatusFilter(value);
    }

    async function confirmCancelCampaign() {
        if (!campaignToCancel) return;

        setCancelingCampaign(true);
        try {
            await emailCampaignsApi.cancel(campaignToCancel.id);
            toast.success('Campanha cancelada.');
            setCampaignToCancel(null);
            fetchCampaigns(page);
        } catch (err) {
            toast.error(getErrorMessage(err, 'Não foi possível cancelar a campanha.'));
        } finally {
            setCancelingCampaign(false);
        }
    }

    return (
        <Section>
            <PanelHeader>
                <PanelHeaderText>
                    <PanelIcon><Megaphone size={17} strokeWidth={2.2} /></PanelIcon>
                    <div>
                        <PanelTitle>Campanhas de e-mail</PanelTitle>
                        <PanelSubtitle>Mande novidades pros seus clientes ou pra quem comprou ingresso de um evento.</PanelSubtitle>
                    </div>
                </PanelHeaderText>

                {can(PERMISSIONS.CAMPAIGNS_SEND) && (
                    <NewCampaignButton type='button' onClick={openComposer}>
                        <Plus size={15} strokeWidth={2.4} />
                        Nova campanha
                    </NewCampaignButton>
                )}
            </PanelHeader>

            <DashCollapse visible={composerOpen} onClose={closeComposer}>
                {composerOpen && (
                    <EmailCampaignComposer
                        key={composerKey}
                        events={events}
                        onCreated={handleCampaignCreated}
                    />
                )}
            </DashCollapse>

            <DashCollapse visible={!!detailCampaignId} onClose={() => setDetailCampaignId(null)}>
                {detailCampaignId && <EmailCampaignDetailPanel campaignId={detailCampaignId} />}
            </DashCollapse>

            <LimitNotice role='note'>
                <Info size={16} strokeWidth={2.2} aria-hidden='true' />
                <div>
                    <strong>Limite de {MAX_CAMPAIGNS_PER_EVENT} disparos em massa por evento.</strong>
                    <br />
                    Cada evento pode ter no máximo {MAX_CAMPAIGNS_PER_EVENT} campanhas de e-mail (agendadas, em andamento ou enviadas).
                    Campanhas canceladas ou que falharam não contam, e campanhas para todos os clientes não entram nesse limite.
                </div>
            </LimitNotice>

            <FilterTabsRow>
                {STATUS_TABS.map(tab => (
                    <FilterTab
                        key={tab.value}
                        type='button'
                        $active={statusFilter === tab.value}
                        onClick={() => changeStatusFilter(tab.value)}
                    >
                        {tab.label}
                    </FilterTab>
                ))}
            </FilterTabsRow>

            {campaignsLoading && <StateText>Carregando...</StateText>}

            {!campaignsLoading && campaignsError && (
                <EmptyState>
                    <EmptyTitle $error>Não foi possível carregar</EmptyTitle>
                    <EmptyHint>{campaignsError}</EmptyHint>
                    <ActionButton type='button' onClick={() => fetchCampaigns(page)}>
                        Tentar novamente
                    </ActionButton>
                </EmptyState>
            )}

            {!campaignsLoading && !campaignsError && campaigns.length === 0 && (
                <EmptyState>
                    <EmptyIcon><Megaphone size={22} strokeWidth={1.8} /></EmptyIcon>
                    <EmptyTitle>
                        {statusFilter ? 'Nenhuma campanha nesse status' : 'Nenhuma campanha enviada ainda'}
                    </EmptyTitle>
                    <EmptyHint>
                        {statusFilter
                            ? 'Tente outro filtro acima.'
                            : 'Use o botão "Nova campanha" para mandar seu primeiro e-mail em massa.'}
                    </EmptyHint>
                </EmptyState>
            )}

            {!campaignsLoading && !campaignsError && campaigns.length > 0 && (
                <>
                    <CampaignList>
                        {campaigns.map(campaign => {
                            const AudienceIcon = AUDIENCE_ICONS[campaign.audience_type] || Users;
                            const meta = STATUS_META[campaign.status] || STATUS_META.canceled;
                            const StatusIcon = meta.icon;
                            const totalRecipients = campaign.total_recipients || 0;
                            const sentPct = totalRecipients > 0 ? Math.round((campaign.sent_count / totalRecipients) * 100) : 0;
                            const failedPct = totalRecipients > 0 ? Math.round((campaign.failed_count / totalRecipients) * 100) : 0;
                            const showProgress = totalRecipients > 0 && ['sending', 'sent', 'failed'].includes(campaign.status);

                            return (
                                <CampaignRow key={campaign.id} onClick={() => setDetailCampaignId(campaign.id)}>
                                    <AudienceBadge>
                                        <AudienceIcon size={16} strokeWidth={2.1} />
                                    </AudienceBadge>

                                    <CampaignRowMain>
                                        <CampaignSubject>{campaign.subject}</CampaignSubject>
                                        <CampaignMeta>
                                            {audienceLabel(campaign)}
                                            {' · '}
                                            {campaign.status === 'scheduled' && campaign.scheduled_at
                                                ? `Agendada para ${formatDateTime(campaign.scheduled_at)}`
                                                : formatDateTime(campaign.created_at)}
                                        </CampaignMeta>
                                        {showProgress && (
                                            <MiniProgressBar>
                                                <MiniProgressFill $pct={sentPct} />
                                                <MiniProgressFillFailed $pct={failedPct} $offset={sentPct} />
                                            </MiniProgressBar>
                                        )}
                                    </CampaignRowMain>

                                    <CampaignRowRight>
                                        <StatusPill $tone={meta.tone}>
                                            <StatusIconWrap $spin={meta.spin}><StatusIcon size={12} strokeWidth={2.4} /></StatusIconWrap>
                                            {meta.label}
                                        </StatusPill>
                                        {campaign.status === 'scheduled' ? (
                                            <CampaignCancelButton
                                                type='button'
                                                onClick={(e) => { e.stopPropagation(); setCampaignToCancel(campaign); }}
                                            >
                                                Cancelar
                                            </CampaignCancelButton>
                                        ) : campaign.status !== 'canceled' ? (
                                            <CampaignRowCount>
                                                {campaign.sent_count}/{totalRecipients}
                                                {campaign.opened_count > 0 && ` · ${campaign.opened_count} abriram`}
                                            </CampaignRowCount>
                                        ) : (
                                            <CampaignRowCount>—</CampaignRowCount>
                                        )}
                                    </CampaignRowRight>

                                    <RowChevron><ChevronRight size={16} /></RowChevron>
                                </CampaignRow>
                            );
                        })}
                    </CampaignList>

                    {lastPage > 1 && (
                        <PaginationBar>
                            <PageInfo>Página {page} de {lastPage} · {total} campanha{total !== 1 ? 's' : ''}</PageInfo>
                            <PaginationButtons>
                                <PageBtn type='button' onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                                    <ChevronLeft size={16} />
                                </PageBtn>
                                <PageBtn type='button' onClick={() => setPage(p => Math.min(lastPage, p + 1))} disabled={page >= lastPage}>
                                    <ChevronRight size={16} />
                                </PageBtn>
                            </PaginationButtons>
                        </PaginationBar>
                    )}
                </>
            )}

            {campaignToCancel && (
                <Modal isOpen={!!campaignToCancel} onClose={() => setCampaignToCancel(null)}>
                    <Column>
                        <Text type='bold'>Cancelar campanha agendada?</Text>
                        <Text name='small' color={colors.nightLight} marginTop='8px'>
                            "{campaignToCancel.subject}" não será mais enviada no horário agendado. Essa ação não pode ser desfeita.
                        </Text>

                        <CancelModalActions>
                            <Button
                                category='outline'
                                variant={cancelingCampaign ? 'disabled' : 'primary'}
                                onClick={() => setCampaignToCancel(null)}
                            >
                                Voltar
                            </Button>
                            {!cancelingCampaign ? (
                                <Button variant='danger' onClick={confirmCancelCampaign}>
                                    Cancelar campanha
                                </Button>
                            ) : (
                                <FullyCentered><Loader size='small' /></FullyCentered>
                            )}
                        </CancelModalActions>
                    </Column>
                </Modal>
            )}
        </Section>
    );
}

/* ─── Layout ─── */

const Section = styled.div`
    background: #fff;
    border-radius: 14px;
    border: 1px solid ${colors.neutralDark};
    overflow: hidden;
`;

const PanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding: 16px 20px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const PanelHeaderText = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
`;

const PanelIcon = styled.span`
    display: inline-flex;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    align-items: center;
    justify-content: center;
    border-radius: 9px;
    margin-top: 1px;
    background: ${panelAccentAlpha(colors.secondaryPurple, '12')};
    color: ${panelAccent(colors.secondaryPurple)};
`;

const PanelTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
`;

const PanelSubtitle = styled.p`
    font-size: 12.5px;
    color: ${colors.nightLight};
    margin: 3px 0 0;
    max-width: 480px;
`;

const NewCampaignButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 14px;
    border-radius: 10px;
    border: none;
    background: ${panelAccent(colors.secondaryPurple)};
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
        opacity: 0.92;
    }
`;

const LimitNotice = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 14px 20px 0;
    padding: 10px 12px;
    border-radius: 10px;
    background: ${colors.warning};
    color: ${colors.nightDark};
    font-size: 12.5px;
    line-height: 1.45;

    svg {
        flex-shrink: 0;
        margin-top: 1px;
    }
`;

const FilterTabsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 14px 20px 0;
`;

const FilterTab = styled.button`
    padding: 6px 13px;
    border-radius: 50px;
    border: 1.5px solid ${({ $active }) => $active ? panelAccent(colors.secondaryPurple) : colors.neutralDark};
    background: ${({ $active }) => $active ? panelAccentAlpha(colors.secondaryPurple, '12') : '#fff'};
    color: ${({ $active }) => $active ? panelAccent(colors.secondaryPurple) : colors.nightLight};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        border-color: ${panelAccent(colors.secondaryPurple)};
    }
`;

const CampaignList = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 6px;
`;

const RowChevron = styled.span`
    flex-shrink: 0;
    display: inline-flex;
    color: ${colors.nightLight};
    transition: transform 0.15s;
`;

const CampaignRow = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 20px;
    cursor: pointer;
    transition: background 0.1s;

    &:hover {
        background: ${colors.neutralBackground};
    }

    &:hover ${RowChevron} {
        transform: translateX(2px);
    }

    & + & {
        border-top: 1px solid ${colors.neutralDark};
    }
`;

const AudienceBadge = styled.span`
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${colors.neutralBackground};
    color: ${colors.night};
`;

const CampaignRowMain = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const CampaignSubject = styled.span`
    font-size: 13.5px;
    font-weight: 600;
    color: ${colors.nightDark};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const CampaignMeta = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const MiniProgressBar = styled.div`
    position: relative;
    height: 4px;
    width: 100%;
    max-width: 220px;
    border-radius: 50px;
    background: ${colors.neutralDark};
    overflow: hidden;
    margin-top: 2px;
`;

const MiniProgressFill = styled.div`
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: ${({ $pct }) => $pct}%;
    background: ${panelAccent(colors.secondaryPurple)};
`;

const MiniProgressFillFailed = styled.div`
    position: absolute;
    left: ${({ $offset }) => $offset}%;
    top: 0;
    bottom: 0;
    width: ${({ $pct }) => $pct}%;
    background: ${colors.danger};
`;

const CampaignRowRight = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 5px;
    flex-shrink: 0;
`;

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

const StatusIconWrap = styled.span`
    display: inline-flex;
    ${({ $spin }) => $spin && css`animation: ${spin} 1s linear infinite;`}
`;

const StatusPill = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 50px;
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    white-space: nowrap;
    background: ${({ $tone }) => $tone === 'danger'
        ? 'rgba(231, 80, 90, 0.12)'
        : $tone === 'success'
            ? 'rgba(43, 200, 102, 0.14)'
            : $tone === 'accent'
                ? panelAccentAlpha(colors.secondaryPurple, '12')
                : colors.neutralBackground};
    color: ${({ $tone }) => $tone === 'danger'
        ? colors.dangerDark
        : $tone === 'success'
            ? colors.successDark
            : $tone === 'accent'
                ? panelAccent(colors.secondaryPurple)
                : colors.nightLight};
`;

const CampaignRowCount = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightLight};
`;

const CampaignCancelButton = styled.button`
    border: none;
    background: transparent;
    padding: 0;
    font-size: 11px;
    font-weight: 700;
    color: ${colors.dangerDark};
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
`;

const CancelModalActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
`;

const StateText = styled.p`
    text-align: center;
    color: ${colors.nightLight};
    font-size: 14px;
    padding: 40px;
    margin: 0;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 48px 24px;
    text-align: center;
    gap: 8px;
`;

const EmptyIcon = styled.span`
    display: inline-flex;
    width: 44px;
    height: 44px;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    margin-bottom: 4px;
    background: ${colors.neutralBackground};
    color: ${colors.nightLight};
`;

const EmptyTitle = styled.p`
    font-weight: 600;
    font-size: 15px;
    color: ${({ $error }) => $error ? colors.danger : colors.nightDark};
    margin: 0;
`;

const EmptyHint = styled.p`
    font-size: 13px;
    color: ${colors.nightLight};
    margin: 0;
`;

const ActionButton = styled.button`
    margin-top: 8px;
    padding: 10px 18px;
    border-radius: 10px;
    border: none;
    background: ${panelAccent(colors.occasionalPurple)};
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        opacity: 0.9;
    }
`;

const PaginationBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 20px;
    border-top: 1px solid ${colors.neutralDark};
`;

const PageInfo = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
`;

const PaginationButtons = styled.div`
    display: flex;
    gap: 8px;
`;

const PageBtn = styled.button`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1.5px solid ${colors.neutralDark};
    background: #fff;
    color: ${colors.nightDark};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background: ${colors.neutralBackground};
    }
`;
